import React from 'react';
import ManagementSectionManager, {
  type ManagerFieldDef,
  type ManagerColumnDef,
} from '@/components/ManagementSectionManager';

/**
 * Artist Roster manager — "Our Artist Roster" (management websites only).
 *
 * Entries auto-synced from the company's artist links are badged "synced
 * from company": they can be hidden/reordered but not deleted manually (the
 * roster sync owns them). Manual entries are fully editable.
 */

const fields: ManagerFieldDef[] = [
  { name: 'artistName', label: 'Artist Name', type: 'text', required: true, maxLength: 120, placeholder: 'Artist Name or Band Name' },
  {
    name: 'profession',
    label: 'Artist Profession',
    type: 'text',
    maxLength: 150,
    placeholder: 'Composer, Singer, Lyricist, etc.',
    helpText: 'Shown as the card subtitle on the website and used by the roster search.',
  },
  {
    name: 'imageUrl',
    label: 'Photo / Thumbnail',
    type: 'image',
    aspectRatio: null,
    helpText: 'Upload any image ratio; keep the full photo or crop freely. The website fits it over a blurred background. Leave empty for a styled placeholder.',
  },
  {
    name: 'isExclusive',
    label: 'Exclusive or Non-exclusive',
    type: 'select',
    // Order + default: Exclusive first (new entries pre-select it; an
    // explicit Non-exclusive on an existing entry is preserved on edit).
    options: [
      { value: 'exclusive', label: 'Exclusive' },
      { value: 'non-exclusive', label: 'Non-exclusive' },
    ],
    fromValue: (value) => (value === false ? 'non-exclusive' : 'exclusive'),
    toValue: (value) => value === 'exclusive',
    helpText: 'Exclusive artists are shown first in the website roster.',
  },
  {
    name: 'websiteUrl',
    label: 'Website URL',
    type: 'url',
    helpText: 'Where the roster card links to. Synced entries default to the artist\'s website.',
  },
];

const columns: ManagerColumnDef[] = [
  {
    name: 'artistName',
    label: 'Artist',
    render: (item) => (
      <div className="flex items-center gap-3">
        {item.imageUrl ? (
          <div className="relative w-16 h-9 rounded-md overflow-hidden ring-1 ring-border shrink-0">
            <img src={item.imageUrl} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full scale-110 object-cover blur-md" />
            <img src={item.imageUrl} alt="" className="absolute inset-0 w-full h-full object-contain" />
          </div>
        ) : (
          <div className="w-16 h-9 rounded-md bg-muted shrink-0" />
        )}
        <span className="font-medium">{item.artistName}</span>
      </div>
    ),
  },
  {
    name: 'profession',
    label: 'Profession',
    render: (item) => <span className="text-muted-foreground">{item.profession || '—'}</span>,
  },
  {
    name: 'isExclusive',
    label: 'Representation',
    render: (item) => (
      <span
        className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
          item.isExclusive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
        }`}
      >
        {item.isExclusive ? 'Exclusive' : 'Non-exclusive'}
      </span>
    ),
  },
];

const ArtistRosterManager: React.FC = () => (
  <ManagementSectionManager
    title="Artist Roster"
    description=""
    apiPath="/api/artist-roster"
    sectionKey="artistRoster"
    itemLabel="artist"
    fields={fields}
    columns={columns}
    badge={(item) =>
      item.source === 'auto' ? { label: 'synced from company', tone: 'muted' } : null
    }
    toggleField={{ name: 'isHidden' }}
    canDelete={(item) => item.source !== 'auto'}
    deleteDisabledReason="Synced entries are managed by the company's artist links — hide them instead."
    emptyHint="No roster entries yet. Artists linked to your company appear here automatically; you can also add entries manually."
  />
);

export default ArtistRosterManager;
